import { StemDifficultyCategory } from '@examdojo/models/question';
import { DerivativeQuestionItemsViewStatus } from '@examdojo/question';
import { QuestionBankStoreModel } from '../data/question-bank-store.model';

export interface QuestionTopicTag {
  id: number;
  code: string;
  name: string;
  isSelected: boolean;
}

export interface QuestionBankUIModel extends QuestionBankStoreModel {
  topics?: QuestionTopicTag[];
}

export interface Pagination {
  pageSize: number;
  page: number;
}

export type PaginationItem = number | 'ellipsis';

export interface PaginationUIModel {
  pageSize: number;
  page: number;
  total: number;
  totalPages: number;
  isPreviousDisabled: boolean;
  isNextDisabled: boolean;
  pageNumbers: PaginationItem[];
  rangeStart: number;
  rangeEnd: number;
}

export enum QuestionBankSortOption {
  MostRelevant = 'most_relevant',
  LeastMarks = 'least_marks',
  MostMarks = 'most_marks',
}

export const QUESTION_BANK_SORT_OPTIONS: QuestionBankSortOption[] = Object.values(QuestionBankSortOption);

export const QUESTION_BANK_SORT_OPTION_TO_LABEL_KEY: Record<QuestionBankSortOption, string> = {
  [QuestionBankSortOption.MostRelevant]: 'examdojo.sort.most_relevant',
  [QuestionBankSortOption.MostMarks]: 'examdojo.sort.most_marks',
  [QuestionBankSortOption.LeastMarks]: 'examdojo.sort.least_marks',
};

export interface QuestionBankFilters {
  courseLevel?: string;
  types?: DerivativeQuestionItemsViewStatus[];
  difficulties?: StemDifficultyCategory[];
  completed?: boolean;
  topicIds?: number[];
  topicMatchMode?: 'ANY' | 'ALL';
  minPoints?: number;
  maxPoints?: number;
  minLatestScore?: number;
  maxLatestScore?: number;
  lastAttemptDateFrom?: Date | null;
  lastAttemptDateTo?: Date | null;
  tags?: string[];
}

export interface QuestionBankAvailableFilters {
  difficulty: boolean;
  type: boolean;
  maxPoints: boolean;
  latestScore: boolean;
  lastAttempted: boolean;
  tags: boolean;
}

export interface MarksRange {
  min: number;
  max: number;
}

export type QuestionTagMap = Record<number, string[]>;

export interface QuestionBankFiltersFormValue {
  completed: boolean;
  difficulties: StemDifficultyCategory[];
  types: DerivativeQuestionItemsViewStatus[];
  minPoints: number;
  maxPoints: number;
  minLatestScore: number;
  maxLatestScore: number;
  lastAttemptDateFrom: Date | null;
  lastAttemptDateTo: Date | null;
  tags: string[];
}

/**
 * Builds an array of page numbers for pagination display.
 *
 * - If totalPages <= 7, returns all pages.
 * - Otherwise, always shows first page, last page, current page and its neighbors,
 *   inserting 'ellipsis' for gaps larger than 2.
 */
export function buildPaginationPageNumbers(currentPage: number, totalPages: number): PaginationItem[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const sorted = [...new Set([1, currentPage - 1, currentPage, currentPage + 1, totalPages])]
    .filter((p) => p >= 1 && p <= totalPages)
    .sort((a, b) => a - b);

  return sorted.flatMap((page, i) => {
    if (i === 0) return [page];
    const gap = page - sorted[i - 1];
    if (gap === 2) return [sorted[i - 1] + 1, page];
    if (gap > 2) return ['ellipsis' as const, page];
    return [page];
  });
}
